import { Injectable, inject } from '@angular/core';
import { AppConfig } from 'client/app/app.config';
import { ConcernSeverity, ConcernSeverityThreshold, CONCERN_SEVERITY, CONCERN_SEVERITY_THRESHOLD } from '../../app/app-constant-types';
import { EventLoggerService } from '@services/event-logger-service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { ConnectionService } from '@services/connection-service';
import { TimeService } from '@services/time-service';
import { ApiClientConstant } from '@cureskin/api-client';
import { LocalStorageService } from '@services/local-storage-service';
import { InstantCheckup } from '@cureskin/api-client/src/server-api';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { firstValueFrom } from 'rxjs';
import { SideFacePhotoBottomSheetComponent } from '@shared/bottom-sheet-layouts/side-face-photo-bottom-sheet/side-face-photo-bottom-sheet.component';
import { ExperimentService } from '@services/experiment';
import { Router } from '@angular/router';
@Injectable({
  providedIn: 'root',
})

export class InstantCheckupService {
  concernSeverity: ConcernSeverity = CONCERN_SEVERITY;
  concernSeverityThreshold:ConcernSeverityThreshold = CONCERN_SEVERITY_THRESHOLD;
  private isRequestPhotoBottomSheetSkipped: boolean = false;
  private userInitialInstantCheckupImagePath?: string;
  private userInitialInstantCheckupImageClass?: string;
  private isFetchingInitialInstantCheckupImage: boolean = false;
  private sideFaceNudgeShownInSession: boolean = false;

  appConfig: AppConfig = inject(AppConfig);
  eventLogger: EventLoggerService = inject(EventLoggerService);
  appWebBridge: AppWebBridgeService = inject(AppWebBridgeService);
  connectionService: ConnectionService = inject(ConnectionService);
  timeService: TimeService = inject(TimeService);
  localStorageService: LocalStorageService = inject(LocalStorageService);
  bottomSheet: MatBottomSheet = inject(MatBottomSheet);
  experimentService: ExperimentService = inject(ExperimentService)
  router: Router = inject(Router);
  constructor() {}

  getSeverityFromConcern(checkup: any):string | object {
    const detectedCount = checkup?.BoundingBoxes?.length || checkup?.PolygonPointsSequences?.length;
    let severity = '';

    if (!detectedCount && checkup.class !== this.appConfig.Shared.InstantCheckup.Concerns.MELASMA) return checkup;
    if (checkup.class === this.appConfig.Shared.InstantCheckup.Concerns.MELASMA) {
      severity = typeof checkup.Severity === 'string' ? checkup.Severity[0].toUpperCase() + checkup.Severity.slice(1).toLowerCase() : '';
    }
    if (detectedCount && this.concernSeverityThreshold[checkup.class] && (
      checkup.class !== this.appConfig.Shared.InstantCheckup.Concerns.MELASMA)) {
      const thresholdRange = this.concernSeverityThreshold[checkup.class];
      if (detectedCount > thresholdRange.moderate) severity = this.concernSeverity.Severe;
      else if (detectedCount > thresholdRange.mild) severity = this.concernSeverity.Moderate;
      else severity = this.concernSeverity.Mild;
    }
    return severity;
  }

  startCapture(extraData: Record<string, any> = {}, tag?: string, fromTakePhotoBanner?: any,): boolean {
    this.eventLogger.cleverTapEvent('instantCheckupInitiated', JSON.stringify({ type: tag }), [
      this.appConfig.Shared.EventTracker.MIXPANEL,
      this.appConfig.Shared.EventTracker.AMPLITUDE,
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    this.appWebBridge.logEventInBranchAndFirebaseFromiOS({
      branch: { name: 'instantCheckupInitiated' },
      firebase: { name: 'instantCheckupInitiated' },
    });
    this.eventLogger.trackEvent('take_photo_clicked', { type: tag });
    this.eventLogger.trackPeopleIncrement({ people_take_photo_clicked: 1 })

    if (this.appWebBridge.isAppWebBridgeLoaded()) {
      this.appWebBridge.notifyCaptureImage(tag, {}, extraData);
      return true;
    }

    return false;
  }

  getInstantCheckupOfType(user: Table.User, days: number, type:string[]): Promise<Table.InstantCheckup[]> {
    return this.connectionService.fetchInstantCheckup({
      userId: user.id,
      limit: 1,
      project: ['type'],
      type,
      dateRange: { startDate: this.timeService.removeDays(new Date(), days), endDate: new Date() },
    });
  }

  async shouldShowRequestPhotoBottomSheet(user: Table.User, photoType: string): Promise<boolean> {
    if (this.getIsRequestPhotoBottomSheetSkipped()) return false;
    const requiredPhotoTypes = this.getRequiredPhotoTypesForReacquisition(photoType);
    if (requiredPhotoTypes.includes(photoType) || this.isInvalidPhotoType(requiredPhotoTypes[0])) return false;
    const instantCheckup = await this.getInstantCheckupOfType(user, 7, requiredPhotoTypes);
    if (instantCheckup.length > 0) {
      return false;
    };
    return true;
  }

  getRequiredPhotoTypesForReacquisition(currentPhotoType: string): Array<string> {
    switch (currentPhotoType) {
      case this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE:
        return [this.appConfig.Shared.InstantCheckup.Type.LEFT_SIDE_FACE, this.appConfig.Shared.InstantCheckup.Type.RIGHT_SIDE_FACE];
      case this.appConfig.Shared.InstantCheckup.Type.LEFT_SIDE_FACE:
        return [this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE];
      case this.appConfig.Shared.InstantCheckup.Type.RIGHT_SIDE_FACE:
        return [this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE];
      default:
        return [this.appConfig.Shared.InstantCheckup.Type.INVALID];
    }
  }

  isInvalidPhotoType(photoType: string): boolean {
    return photoType === this.appConfig.Shared.InstantCheckup.Type.INVALID;
  }

  setIsRequestPhotoBottomSheetSkipped(value: boolean): void {
    this.isRequestPhotoBottomSheetSkipped = value;
  }

  getIsRequestPhotoBottomSheetSkipped(): boolean {
    return this.isRequestPhotoBottomSheetSkipped
  }

  isSideFaceNudgeShownInSession(): boolean {
    return this.sideFaceNudgeShownInSession;
  }

  getValidInstantCheckupTypes(): string[] {
    return Object.keys(this.appConfig.Shared.InstantCheckup.Type).filter((type_: string): boolean => {
      return ![this.appConfig.Shared.InstantCheckup.Type.INVALID,
        this.appConfig.Shared.InstantCheckup.Type.REPORT,
        this.appConfig.Shared.InstantCheckup.Type.BODY,
        this.appConfig.Shared.InstantCheckup.Type.OTHER].includes(type_);
    });
  }

  async getUserInitialInstantCheckupImage(userId: string, activeRegimen: any, regimenClass?: string): Promise<string | undefined> {
    const resolvedClass = regimenClass || 'FACE';
    if (this.userInitialInstantCheckupImagePath && this.userInitialInstantCheckupImageClass === resolvedClass) {
      return this.userInitialInstantCheckupImagePath;
    }
    if (this.isFetchingInitialInstantCheckupImage) return this.userInitialInstantCheckupImagePath;

    if (!userId || !activeRegimen) return undefined;

    // Clear cache if class changed
    if (this.userInitialInstantCheckupImageClass && this.userInitialInstantCheckupImageClass !== resolvedClass) {
      this.userInitialInstantCheckupImagePath = undefined;
    }

    this.isFetchingInitialInstantCheckupImage = true;
    this.userInitialInstantCheckupImageClass = resolvedClass;
    try {
      const isHair = resolvedClass === 'HAIR';
      const photoTypes = isHair
        ? [
            this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT,
            this.appConfig.Shared.InstantCheckup.Type.HAIR_TOP,
            this.appConfig.Shared.InstantCheckup.Type.HAIR,
          ]
        : [
            this.appConfig.Shared.InstantCheckup.Type.LEFT_SIDE_FACE,
            this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE,
            this.appConfig.Shared.InstantCheckup.Type.RIGHT_SIDE_FACE,
          ];
      const preferredType = isHair
        ? this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT
        : this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE;

      const instantCheckups = await this.connectionService.fetchInstantCheckup({
        userId,
        type: photoTypes,
        limit: 50,
        sortAscending: true,
      });

      if (!instantCheckups || instantCheckups.length === 0) {
        return undefined;
      }

      const activeFromIso = activeRegimen.activeFromDate?.iso ?? activeRegimen.activeFrom?.iso;
      let candidateCheckups: Array<any> = instantCheckups;
      if (activeFromIso) {
        const activeFrom = new Date(activeFromIso);
        const startOfDay = new Date(activeFrom);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(activeFrom);
        endOfDay.setHours(23, 59, 59, 999);

        const inRange = instantCheckups.filter((checkup: any): boolean => {
          const created = checkup.createdAt?.iso ?? checkup.createdAt;
          if (!created) return false;
          const createdAt = new Date(created);
          return createdAt >= startOfDay && createdAt <= endOfDay;
        });

        if (inRange.length) {
          candidateCheckups = inRange;
        }
      }

      const preferredCheckup = candidateCheckups.find((checkup: any): boolean =>
        checkup.type === preferredType);

      const chosenCheckup = preferredCheckup ?? candidateCheckups[0];
      this.userInitialInstantCheckupImagePath = chosenCheckup?.imagePath;
      return this.userInitialInstantCheckupImagePath;
    } catch (e) {
      return undefined;
    } finally {
      this.isFetchingInitialInstantCheckupImage = false;
    }
  }

  clearInitialInstantCheckupImageCache(): void {
    this.userInitialInstantCheckupImagePath = undefined;
    this.userInitialInstantCheckupImageClass = undefined;
    this.isFetchingInitialInstantCheckupImage = false;
  }


  async validateAndOpenSideFacePhotoBottomSheet(data: {
    currentInstantCheckup: InstantCheckup,
    isReacquisitionUser: boolean,
    isConsultationOrFollowup: boolean,
    userId: string,
    callback: () => void | Promise<void>,
  }): Promise<string | undefined> {
    const { currentInstantCheckup, isReacquisitionUser, isConsultationOrFollowup, userId, callback } = data;

    if (!this.experimentService.sidePhotoNudgeInFrontFaceExperiment?.enable) return undefined

    if (isReacquisitionUser || !isConsultationOrFollowup) return undefined;

    const isCurrentPhotoFrontFace = currentInstantCheckup.type === ApiClientConstant.InstantCheckup.Type.FRONT_FACE;
    if (!isCurrentPhotoFrontFace) return undefined;


    if (this.sideFaceNudgeShownInSession || this.localStorageService.getBooleanValue('sideFaceUploadSkipped')) return undefined;

    const hasSideFacePhoto = await this.hasRecentSideFacePhoto(userId);

    if (!hasSideFacePhoto) {
      const availableImages = [{ image: currentInstantCheckup.imagePath, side: currentInstantCheckup.type }];
      const bottomSheetRef = this.bottomSheet.open(SideFacePhotoBottomSheetComponent, {
        data: {
          availableImages,
          requiredSide: ApiClientConstant.InstantCheckup.Type.SIDE_FACE,
          callback: (): void | Promise<void> => {
            return Promise.resolve(callback());
          },
        },
        disableClose: true,
      });
      bottomSheetRef.backdropClick().subscribe((): void => {
        this.sideFaceNudgeShownInSession = true;
        bottomSheetRef.dismiss(SideFacePhotoBottomSheetComponent.DISMISS_SKIP);
      });

      const result = await firstValueFrom(bottomSheetRef.afterDismissed());
      this.sideFaceNudgeShownInSession = true;
      return result as string | undefined;
    }
    return undefined;
  }

  private async hasRecentSideFacePhoto(userId: string): Promise<boolean> {
    const sideFaceTypes = [
      ApiClientConstant.InstantCheckup.Type.SIDE_FACE,
      ApiClientConstant.InstantCheckup.Type.LEFT_SIDE_FACE,
      ApiClientConstant.InstantCheckup.Type.RIGHT_SIDE_FACE,
    ];
    const fiveDaysAgo = this.timeService.removeDays(new Date(), 5);
    const recentSideFacePhotos: InstantCheckup[] = await this.connectionService.fetchInstantCheckup({
      userId,
      type: sideFaceTypes,
      dateRange: { startDate: fiveDaysAgo, endDate: new Date() },
      limit: 1,
      project: ['type'],
    });
    return recentSideFacePhotos.length > 0;
  }

  async navigateToInstantCheckupPreview(queryParams: Record<string, unknown>): Promise<void> {
    await this.router.navigate(['/user/instantCheckup/preview'], { queryParams });
  }
}
